import { useQuery } from '@tanstack/react-query';
import { useOrganization } from '@/hooks/useOrganization';
import { messagesService } from '@/services/messages/messagesService';
import { platformMessagesService } from '@/services/platformMessages/platformMessagesService';
import { queryKeys } from '@/lib/queryKeys';
import type { HeaderMessageItem } from '@/types/platformMessages';

function sortHeaderMessages(messages: HeaderMessageItem[]): HeaderMessageItem[] {
  const priority = (message: HeaderMessageItem) => {
    const sourceScore = message.source === 'platform' ? 0 : 1;
    const urgentScore = message.isUrgent ? 0 : 1;
    return sourceScore * 2 + urgentScore;
  };

  return [...messages].sort((a, b) => {
    const priorityDiff = priority(a) - priority(b);
    if (priorityDiff !== 0) return priorityDiff;
    return b.createdAt.getTime() - a.createdAt.getTime();
  });
}

function toHeaderMessageItem(
  message: {
    id: string;
    messageText: string;
    expiresAt: Date;
    isUrgent: boolean;
    createdAt: Date;
  },
  source: HeaderMessageItem['source'],
  isDismissible: boolean,
): HeaderMessageItem {
  return {
    id: message.id,
    messageText: message.messageText,
    expiresAt: message.expiresAt,
    isUrgent: message.isUrgent,
    isDismissible,
    source,
    createdAt: message.createdAt,
  };
}

export function headerMessageKey(message: HeaderMessageItem): string {
  return `${message.source}:${message.id}`;
}

export function useHeaderMessages() {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id ?? '';
  const slug = currentOrganization?.slug ?? '';

  const {
    data: activeMessages = [],
    refetch,
  } = useQuery<HeaderMessageItem[]>({
    queryKey: queryKeys.headerMessages(orgId, slug),
    queryFn: async () => {
      const [orgMessages, platformMessages] = await Promise.all([
        messagesService.getActiveMessages(currentOrganization!.id),
        platformMessagesService.getActiveForOrganization(slug),
      ]);

      const merged = [
        ...platformMessages.map((message) =>
          toHeaderMessageItem(message, 'platform', message.isDismissible),
        ),
        ...orgMessages.map((message) =>
          toHeaderMessageItem(message, 'organization', true),
        ),
      ];

      return sortHeaderMessages(merged);
    },
    enabled: !!currentOrganization?.id && !!slug,
    staleTime: 0,
    refetchInterval: 30 * 60 * 1000, // 30 minutes
    refetchIntervalInBackground: false,
  });

  return { activeMessages, refetch };
}
